import { useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/Button';
import { Card, CardHeader } from '@/components/Card';
import { abrirChamado, catalogo } from '@/portal/portal.api';

/**
 * Abertura de chamado a partir do catálogo.
 *
 * O serviço vem de uma lista que o servidor montou — filtrada pelo contrato do
 * cliente — e o `servicoId` escolhido é reconferido lá na abertura. A lista na
 * tela é conveniência; a peneira é do servidor.
 *
 * O formulário não tem campo de prioridade, de responsável, de equipe nem de
 * organização. Urgência é a percepção do cliente; prioridade é a decisão da
 * Aptum, derivada da matriz impacto × urgência. Deixar o cliente escolher a
 * prioridade faria todo chamado nascer crítico em três semanas.
 */
export function AbrirChamadoPage() {
  const navegar = useNavigate();
  const servicos = useQuery({ queryKey: ['portal', 'catalogo'], queryFn: catalogo });

  const [servicoId, setServicoId] = useState('');
  const [assunto, setAssunto] = useState('');
  const [descricao, setDescricao] = useState('');
  const [tipo, setTipo] = useState<'INCIDENT' | 'REQUEST'>('REQUEST');
  const [urgencia, setUrgencia] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'>('MEDIUM');

  const abrir = useMutation({
    mutationFn: () =>
      abrirChamado({
        servicoId,
        assunto,
        ...(descricao.trim() ? { descricaoHtml: `<p>${escaparHtml(descricao)}</p>` } : {}),
        tipo,
        urgencia,
      }),
    onSuccess: (criado) => {
      void navegar({ to: '/chamados/$id', params: { id: criado.id } });
    },
  });

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    if (servicoId && assunto.trim().length >= 3) abrir.mutate();
  }

  return (
    <Card className="mx-auto max-w-2xl">
      <CardHeader
        title="Abrir chamado"
        description={
          servicos.data?.fonte === 'CATALOGO'
            ? 'Os serviços abaixo são o catálogo geral — sua empresa ainda não tem contrato vigente cadastrado.'
            : 'Escolha o serviço contratado a que o pedido se refere.'
        }
      />

      <form className="space-y-4 px-5 py-4" onSubmit={submeter}>
        <label className="block">
          <span className="text-sm font-medium">Serviço</span>
          <select
            required
            value={servicoId}
            onChange={(e) => setServicoId(e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-border bg-background px-2 text-sm"
          >
            <option value="">Selecione…</option>
            {servicos.data?.items.map((servico) => (
              <option key={servico.id} value={servico.id}>
                {servico.nome}
              </option>
            ))}
          </select>
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-sm font-medium">Tipo</span>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value as 'INCIDENT' | 'REQUEST')}
              className="mt-1 h-10 w-full rounded-md border border-border bg-background px-2 text-sm"
            >
              <option value="REQUEST">Pedido</option>
              <option value="INCIDENT">Algo parou de funcionar</option>
            </select>
          </label>

          <label className="block">
            <span className="text-sm font-medium">Urgência</span>
            <select
              value={urgencia}
              onChange={(e) => setUrgencia(e.target.value as typeof urgencia)}
              className="mt-1 h-10 w-full rounded-md border border-border bg-background px-2 text-sm"
            >
              <option value="LOW">Posso esperar</option>
              <option value="MEDIUM">Normal</option>
              <option value="HIGH">Estou impedido</option>
              <option value="CRITICAL">A empresa parou</option>
            </select>
          </label>
        </div>

        <label className="block">
          <span className="text-sm font-medium">Assunto</span>
          <input
            required
            minLength={3}
            maxLength={300}
            value={assunto}
            onChange={(e) => setAssunto(e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium">O que aconteceu</span>
          <textarea
            rows={5}
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            className="mt-1 w-full rounded-md border border-border bg-background p-2 text-sm"
          />
        </label>

        {abrir.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {(abrir.error as Error).message}
          </p>
        ) : null}

        <Button type="submit" loading={abrir.isPending}>
          Abrir chamado
        </Button>
      </form>
    </Card>
  );
}

/**
 * Escapa o texto antes de embrulhá-lo em `<p>`.
 *
 * O servidor higieniza de novo na gravação — é lá que a decisão vale, porque
 * o cliente controla o corpo da requisição. Escapar aqui evita que o texto do
 * usuário chegue lá já quebrado por um `<` que ele digitou de propósito.
 */
function escaparHtml(texto: string): string {
  return texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
}
